import { MyClipQuery, useRenderClipMutation } from "@/generated/graphql";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CropSubtitleEditor } from '@kassellabs/autoeditor'
import { toast } from "sonner";

import { useMyDataQuery } from "@/generated/graphql";
import useSubtitlePresets from "@/hooks/use-subtitle-presets";
import { getVideoMetadata } from "@/lib/video-metadata";
import { getPresetedExportData } from "@/lib/get-legacy-preseted-export-data";
import { Download } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { GET_MY_CLIP } from "@/queries/queries";

const AutoEditor = ({ clip }: { clip: MyClipQuery['myClip'] }) => {
  const { t } = useTranslation();
  const [exportData, setExportData] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const { data: subtitlePresets, loading: subtitlePresetsLoading } = useSubtitlePresets()
  const { data: myData } = useMyDataQuery()
  const navigate = useNavigate()
  const [renderClipMutation, { loading: renderLoading }] = useRenderClipMutation()

  const videoUrl = clip.clipped_video_url || clip.finished_video_url

  useEffect(() => {
    if (!myData?.myData || subtitlePresetsLoading || !videoUrl) {
      return () => { }
    }

    let cropSubtitleEditor = null
    getVideoMetadata(videoUrl).then((metadata) => {
      const clipWithVideoUrl = { ...clip, clipped_video_url: videoUrl }
      const presetExportData = getPresetedExportData(clipWithVideoUrl, metadata, subtitlePresets)
      const renderProps = clip?.data?.render || {}
      cropSubtitleEditor = new CropSubtitleEditor('#autoeditor-editor', {
        apiKey: import.meta.env.VITE_AUTOEDITOR_API_KEY,
        userId: myData?.myData?.email,
        detectScenes: false,
        defaultMode: 'subtitle',
        theme: {
          palette: {
            primary: {
              main: '#caff1f',
              contrastText: 'rgba(0, 0, 0, 0.88)',
            },
            secondary: {
              main: '#2b2c33',
              contrastText: '#fff',
            },
            background: {
              default: '#0b0c0f',
            }
          },
        },
        input: presetExportData.input,
        subtitle: presetExportData.subtitle,
        crop: presetExportData.crop,
        ...renderProps,
        data: {
          ...presetExportData.data,
          ...renderProps?.data,
          overlays: {
            ...presetExportData.data.overlays,
            ...renderProps?.data?.overlays,
            front: [
              ...(presetExportData?.data?.overlays?.front || []),
              ...(renderProps?.data?.overlays?.front || []),
              // {
              //   type: 'image',
              //   url: 'https://app.autoclipper.live/images/autoclipper-watermark.png',
              //   originalURL: 'https://app.autoclipper.live/images/autoclipper-watermark.png',
              //   settings: {
              //     crop: { x: 38, y: -148 },
              //     zoom: 0.1,
              //     rotation: 0,
              //     mediaSize: {
              //       width: 1000,
              //       height: 268.77470355731225,
              //       naturalWidth: 1012,
              //       naturalHeight: 272,
              //     },
              //     cropArea: {
              //       width: 1945,
              //       height: 3457,
              //       x: -851,
              //       y: -95,
              //     },
              //     sizeRatio: 0.6248192074052646,
              //   },
              //   start: 0,
              //   end: 5,
              // },
            ].filter((item) => !item.watermark),
          },
        },
      })

      cropSubtitleEditor.on('ready', ({
        input, data, crop, subtitle,
      }) => {
        setIsReady(true)
        console.log('ready', input, data, crop, subtitle)
        setExportData({
          input,
          data,
          crop,
          subtitle,
        })
      })

      cropSubtitleEditor.on('change', ({
        input, data, crop, subtitle,
      }) => {
        console.log('change', input, data, crop, subtitle)
        setExportData({
          input,
          data,
          crop,
          subtitle,
        })
      })
    })

    return () => {
      setIsReady(false)
      setExportData(null)
      if (cropSubtitleEditor) {
        cropSubtitleEditor.destroy()
      }
    }
  }, [myData, subtitlePresets, subtitlePresetsLoading, clip, videoUrl])

  const handleRenderClip = async () => {
    if (!exportData || !clip?.id) {
      console.error('Export data or clip ID is missing')
      return
    }

    try {
      await renderClipMutation({
        variables: {
          id: clip.id,
          input: exportData
        },
        refetchQueries: [
          {
            query: GET_MY_CLIP,
            variables: {
              id: clip.id
            }
          }
        ]
      })

      toast.success(t('autoEditor.renderSuccess'))
      navigate(`/clips`)
    } catch (error) {
      console.error('Failed to render clip:', error)
      toast.error(t('autoEditor.renderError'))
    }
  }

  return (
    <>
      <div className="p-4 flex items-center justify-between">
        <h1 className="text-xl lg:text-2xl font-bold">{t('autoEditor.editClip', { name: clip.name })}</h1>
        <div className="p-4 flex items-center justify-between">
          <Button
            variant="gradient"
            size="sm"
            disabled={!isReady || !exportData || renderLoading}
            onClick={handleRenderClip}
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">
              {renderLoading
                ? t('autoEditor.startingGeneration')
                : (isReady && exportData ? t('autoEditor.generateClip') : t('autoEditor.loadingClip'))
              }
            </span>
          </Button>
        </div>
      </div>
      <div
        id="autoeditor-editor"
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <div className={`flex items-center justify-center min-h-[60vh] ${isReady ? 'hidden' : ''}`}>
          <div className="text-center space-y-2">
            <div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">{t('autoEditor.loadingClip')}</p>
          </div>
        </div>
      </div>
      <style>
        {`
          #autoeditor-editor iframe {
            display: none;
          }
          #autoeditor-editor iframe:first-of-type {
            display: block;
          }
        `}
      </style>
    </>
  );
};

export default AutoEditor;